/**
 * InvestmentContractsSection Component
 *
 * Section 3 of the Gestão de Recursos Edit Page:
 * "Composição de Investimentos e Patrocínios Contratados"
 *
 * Features:
 * - Dropdown to select funding source (18 options)
 * - Input for investor/sponsor name
 * - CurrencyInput for contract amount
 * - Add Contract button to append to table
 * - Dynamic table showing added contracts
 * - Delete button for each contract row
 * - Auto-calculated total at bottom
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Add button: Primary purple #7846FF
 * - Delete button: Error red #DC4426
 * - Total row: Success green #13D480
 */

import { useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InvestmentContractItem } from "@/types/gestao-recursos.interface";
import { GestaoRecursosEditForm } from "@/pages/financeiro/gestao-recursos-editar";

// Same 18 funding sources as Financing Plan
const FUNDING_SOURCES = [
  "Lei nº 8.685/93 - art 1º",
  "Lei nº 8.685/93 - art 1º-A",
  "Lei nº 8.685/93 - art 3º",
  "Lei nº 8.685/93 - art 3º-A",
  "Contribuição de FUNCINES (investimento)",
  "Patrocínio Pessoa Física",
  "Patrocínio Pessoa Jurídica",
  "Público - Federal",
  "Público - Estadual",
  "Público - Municipal",
  "Lei Estadual de Incentivo",
  "Lei Municipal de Incentivo",
  "Patrocínio Privado",
  "Prêmio Internacional",
  "Programa Internacional",
  "Licença de Exibição",
  "Contribuição não financeira",
  "Outros",
];

interface InvestmentContractsSectionProps {
  form: UseFormReturn<GestaoRecursosEditForm>;
}

export function InvestmentContractsSection({
  form,
}: InvestmentContractsSectionProps) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "investmentContracts",
  });

  // Local state for add contract form
  const [newContract, setNewContract] = useState({
    fonte: "",
    investidorPatrocinador: "",
    valor: 0,
  });

  // Handle add contract
  const handleAddContract = () => {
    if (
      newContract.fonte &&
      newContract.investidorPatrocinador &&
      newContract.valor > 0
    ) {
      append(newContract);
      // Reset form
      setNewContract({
        fonte: "",
        investidorPatrocinador: "",
        valor: 0,
      });
    }
  };

  // Watch all contracts to calculate total
  const watchedContracts = form.watch("investmentContracts");
  const totalInvestments =
    watchedContracts?.reduce((sum, item) => sum + (item?.valor || 0), 0) || 0;

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[40px]">
      {/* Section Title */}
      <h3 className="text-[20px] font-bold text-[#F6F6F6] mb-[30px]">
        Composição de Investimentos e Patrocínios Contratados
      </h3>

      {/* Add Contract Form */}
      <div className="mb-[30px] grid grid-cols-[2fr_2fr_1.5fr_auto] gap-[20px] items-end">
        {/* Funding Source Dropdown */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Fonte de Financiamento
          </label>
          <Select
            value={newContract.fonte}
            onValueChange={(value) =>
              setNewContract({ ...newContract, fonte: value })
            }
          >
            <SelectTrigger className="bg-gray-800 text-white border-gray-600">
              <SelectValue placeholder="Selecione a fonte..." />
            </SelectTrigger>
            <SelectContent>
              {FUNDING_SOURCES.map((source) => (
                <SelectItem key={source} value={source}>
                  {source}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Investor/Sponsor Name */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">
            Investidor/Patrocinador
          </label>
          <Input
            value={newContract.investidorPatrocinador}
            onChange={(e) =>
              setNewContract({
                ...newContract,
                investidorPatrocinador: e.target.value,
              })
            }
            placeholder="Nome do investidor ou patrocinador..."
            className="bg-gray-800 text-white border-gray-600"
          />
        </div>

        {/* Contract Amount */}
        <div className="flex flex-col gap-2">
          <label className="text-[14px] text-[#F6F6F6]">Valor</label>
          <div className="flex items-center w-full px-3 py-2 rounded-md bg-gray-800 text-white border border-gray-600">
            <input
              type="text"
              value={
                newContract.valor > 0
                  ? new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(newContract.valor)
                  : ""
              }
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^\d,]/g, "");
                const withDot = cleaned.replace(",", ".");
                const parsed = parseFloat(withDot);
                setNewContract({
                  ...newContract,
                  valor: isNaN(parsed) ? 0 : parsed,
                });
              }}
              placeholder="R$ 0,00"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder-gray-400"
            />
          </div>
        </div>

        {/* Add Button */}
        <Button
          type="button"
          onClick={handleAddContract}
          disabled={
            !newContract.fonte ||
            !newContract.investidorPatrocinador ||
            newContract.valor <= 0
          }
          className="bg-[#7846FF] hover:bg-[#9b6fff] text-white h-[42px] px-[20px] text-[14px] font-bold"
        >
          <Plus size={18} className="mr-2" />
          Adicionar Contrato
        </Button>
      </div>

      {/* Contracts Table */}
      {fields.length > 0 && (
        <div className="border border-[#5D5D5D] rounded-[10px] overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[2fr_2fr_1.5fr_auto] bg-[#0C1427] border-b border-[#5D5D5D]">
            <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6] border-r border-[#5D5D5D]">
              Fonte de Financiamento
            </div>
            <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6] border-r border-[#5D5D5D]">
              Investidor/Patrocinador
            </div>
            <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6] border-r border-[#5D5D5D]">
              Valor
            </div>
            <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#F6F6F6]">
              Ações
            </div>
          </div>

          {/* Table Rows */}
          {fields.map((field, index) => {
            const contract = watchedContracts?.[index];
            return (
              <div
                key={field.id}
                className="grid grid-cols-[2fr_2fr_1.5fr_auto] border-b border-[#5D5D5D] last:border-b-0"
              >
                {/* Funding Source */}
                <div className="px-[20px] py-[16px] text-[14px] text-[#F6F6F6] border-r border-[#5D5D5D] flex items-center">
                  {contract?.fonte}
                </div>

                {/* Investor/Sponsor */}
                <div className="px-[20px] py-[16px] text-[14px] text-[#F6F6F6] border-r border-[#5D5D5D] flex items-center">
                  {contract?.investidorPatrocinador}
                </div>

                {/* Value */}
                <div className="px-[20px] py-[16px] text-[14px] text-[#F6F6F6] border-r border-[#5D5D5D] flex items-center font-bold">
                  {new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(contract?.valor || 0)}
                </div>

                {/* Delete Button */}
                <div className="px-[20px] py-[16px] flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-[#DC4426] hover:text-[#ff5733] transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Total Row */}
          <div className="grid grid-cols-[2fr_2fr_1.5fr_auto] bg-[#0C1427]">
            <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#13D480] border-r border-[#5D5D5D] col-span-2">
              Total de Investimentos e Patrocínios
            </div>
            <div className="px-[20px] py-[16px] text-[14px] font-bold text-[#13D480] border-r border-[#5D5D5D]">
              {new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(totalInvestments)}
            </div>
            <div className="px-[20px] py-[16px]"></div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {fields.length === 0 && (
        <div className="text-center py-[40px] text-[#B0B0B0] text-[14px]">
          Nenhum contrato adicionado. Use o formulário acima para adicionar
          contratos de investimento e patrocínio.
        </div>
      )}
    </div>
  );
}
